import { selectLayout, THEME } from '@src/store/layout/layoutSlice';
import { Button, Drawer, Modal } from 'antd';
import clsx from 'clsx';
import { CloseCircle } from 'iconsax-react';
import { ReactNode, useEffect, useState } from 'react';
import { useSelector } from 'react-redux';

interface ResponsiveModalProps {
  isOpen: boolean;
  title?: string;
  headerIcon?: ReactNode;
  content: ReactNode;
  footer?: ReactNode;
  onClose: () => void;
  className?: string;
}

export function ResponsiveModal({
  isOpen,
  title,
  headerIcon,
  content,
  footer,
  onClose,
  className,
}: ResponsiveModalProps) {
  const layout = useSelector(selectLayout);
  const [isMobile, setIsMobile] = useState(false);

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth < 640); // Mobile breakpoint (sm)
    };

    handleResize();
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  return isMobile ? (
    <Drawer
      push={false}
      open={isOpen}
      onClose={onClose}
      closeIcon={null}
      placement='bottom'
      height='auto'
      className={clsx('rounded-t-2xl flex flex-col max-h-[90vh]', className)}
      styles={{
        content: {
          background: 'var(--wl-surface, #ffffff)',
        },
        mask: {
          backdropFilter: 'blur(4px)',
          backgroundColor: 'rgba(25, 28, 30, 0.3)',
        },
        body: {
          display: 'flex',
          flexDirection: 'column',
          overflow: 'auto',
        },
        wrapper: {
          borderTopLeftRadius: '1rem',
          borderTopRightRadius: '1rem',
        },
      }}
      footer={footer && <div className='z-10'>{footer}</div>}
    >
      <div className='flex items-center justify-between mb-4'>
        <div className='flex items-center gap-2 text-lg text-wl-on-background'>
          {headerIcon && <span className='text-xl'>{headerIcon}</span>}
          {title && <span className='text-M-20'>{title}</span>}
        </div>
        <Button
          type='text'
          icon={
            <CloseCircle
              size={24}
              color={layout.theme === THEME.DARK ? '#fff' : '#000'}
            />
          }
          onClick={onClose}
        />
      </div>
      <div className='flex-grow overflow-auto'>{content}</div>
    </Drawer>
  ) : (
    <Modal
      open={isOpen}
      closeIcon={null}
      footer={footer ?? null}
      centered
      classNames={{ content: 'dark:bg-black' }}
      styles={{
        mask: {
          backdropFilter: 'blur(4px)',
          backgroundColor: 'rgba(25, 28, 30, 0.3)',
        },
        content: {
          background: 'var(--wl-surface, #ffffff)',
          padding: '24px',
          borderRadius: '20px',
        },
        body: {
          padding: 0,
          overflow: 'hidden',
          borderRadius: '0.5rem',
          maxHeight: '80vh',
        },
      }}
      className={clsx('custom-modal', className)}
    >
      <div className='sticky top-0 z-10 dark:text-white flex items-center justify-between mb-4'>
        <div className='flex items-center gap-2 text-lg'>
          {headerIcon && <span className='text-xl'>{headerIcon}</span>}
          {title && <span className='text-M-20'>{title}</span>}
        </div>
        <Button
          type='text'
          className='w-fit'
          icon={
            <CloseCircle
              size={24}
              color={layout.theme === THEME.DARK ? '#fff' : '#000'}
            />
          }
          onClick={onClose}
        />
      </div>
      <div
        className='flex-grow overflow-auto'
        style={{ maxHeight: 'calc(80vh - 60px)' }}
      >
        {content}
      </div>
    </Modal>
  );
}
